import { Country, EntityLabel, Region } from '@esg/esg-global-types';
import { auth } from '../google/firebase';
import { apollo_client, BatchWrite } from '../app/db_util';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { validateMasterListParams } from '../../util/validation';
import { ALLOW_DELETE_REGIONS_QUERY, GET_REGIONS_QUERY } from '../graphql/queries/region';
import {
  ARCHIVE_REGION_MUTATOR,
  CREATE_REGION_MUTATOR,
  DELETE_REGION_MUTATOR,
  UNARCHIVE_REGION_MUTATOR,
  UPDATE_REGION_MUTATOR
} from '../graphql/mutations/region';

export type RegionData = Omit<Region, 'id'>;

/**
 * Type guard — narrows an unknown entity to Region.
 */
export function isRegion(entity: unknown): entity is Region {
  return (
    typeof entity === 'object' &&
    entity !== null &&
    'id' in entity &&
    'name' in entity &&
    'country' in entity
  );
}

export interface RegionExtended extends Region {
  country: Country;
}

// Singular and plural label for model entity.
export const region_label: EntityLabel = {
  one: 'Region',
  many: 'Regions'
};

// Singular and plural label for general location entity.
export const location_label: EntityLabel = {
  one: 'Location',
  many: 'Locations'
};

/**
 * Create a collection path string for the master list regions or configured regions.
 * @param {boolean} master_list Reference the master list collection.
 * @param {string} group_id When not referencing a master list collection, a group id for the configured group is required.
 * @param {string} company_id When not referencing a master list collection, a company id for the configured company is required.
 * @returns {string} Path to a countries collection.
 */
export const createRegionCollectionPath = (
  master_list: boolean,
  group_id?: string,
  company_id?: string
): string => {
  try {
    validateMasterListParams(master_list, group_id, company_id);
    return master_list
      ? `/region_master_list`
      : `groups/${group_id}/companies/${company_id}/regions`;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: createRegionCollectionPath failed on an unknown error.',
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Query all region documents for master list or current portal company joined with relative region data
 * @param {boolean} master_list Return master list or configured region documents.
 * @param {string} group_id Group object of current portal company
 * @param {string} company_id id of current portal company to reference firestore documents
 * @param {string} id query a specific region by id.
 * @returns {Promise<Array<Region>>}
 */
export const getRegions = async (
  master_list: boolean,
  group_id?: string,
  company_id?: string,
  id?: string
): Promise<Array<Region>> => {
  try {
    const regions: Array<Region> = await apollo_client
      .query({
        variables: {
          master_list: master_list,
          group_id: group_id,
          company_id: company_id,
          id: id
        },
        query: GET_REGIONS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.regions;
      });
    return regions;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: getRegions failed on an unknown error.',
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id
      },
      tracking_id
    );
  }
};

/**
 * Create Region data in firestore on master list or company level.
 * @param {boolean} master_list Flag to create new Regions within master list or company level collection.
 * @param {Array<RegionExtended | RegionData>} regions list of new regions to be added to the database.
 * @param {string | undefined} group_id Optional ID of Group to create regions for.
 * @param {string | undefined} company_id Optional ID of Company to create regions for.
 * @returns {Promise<Array<BatchWrite>>}
 */
export const createRegions = async (
  master_list: boolean,
  regions: Array<RegionExtended | RegionData>,
  group_id?: string,
  company_id?: string,
  return_batch?: boolean
): Promise<Array<BatchWrite>> => {
  try {
    const region_writes: Array<BatchWrite> = await apollo_client
      .mutate({
        mutation: CREATE_REGION_MUTATOR,
        variables: {
          master_list: master_list,
          regions: regions,
          group_id: group_id,
          company_id: company_id,
          user_email: auth.currentUser?.email,
          return_batch: return_batch
        }
      })
      .then((response) => {
        return response.data.createRegions;
      });
    return region_writes;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/region.ts failed on an unknown error while calling createRegions.',
      {
        master_list: master_list,
        regions: regions,
        group_id,
        company_id
      },
      tracking_id
    );
  }
};

/**
 * Update region with relative data
 * @param {boolean} master_list Flag to update region in master list
 * @param {Region} updated_region New Region data to push into document
 * @param {Region} original_region Old Region data
 * @param {string} group_id Optional ID of Group to update region for
 * @param {string} company_id Optional ID of Company to update region for
 * @returns {Promise<void>}
 */
export const updateRegion = async (
  master_list: boolean,
  updated_region: Region,
  original_region: Region,
  group_id?: string,
  company_id?: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: UPDATE_REGION_MUTATOR,
        variables: {
          master_list: master_list,
          updated_region: updated_region,
          original_region: original_region,
          group_id: group_id,
          company_id: company_id,
          user: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.updateRegions;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/region.ts failed on an unknown error while calling updateRegion.',
      {
        group_id: group_id,
        company_id: company_id,
        updated_region: updated_region,
        original_region: original_region
      },
      tracking_id
    );
  }
};

/**
 * Soft delete a Region.
 * @param {string} group_id ID of Group to delete region for
 * @param {string} company_id ID of Company to delete region for
 * @param {string} region_id ID of region to delete
 * @param {string} region_group_id ID of region group to find delete region in
 * @returns {void}
 */
export const deleteRegion = async (
  master_list: boolean,
  region_id: string,
  region_name: string,
  group_id?: string,
  company_id?: string
): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_REGION_MUTATOR,
        variables: {
          master_list: master_list,
          region_id: region_id,
          region_name: region_name,
          group_id: group_id,
          company_id: company_id,
          user_email: auth.currentUser?.email
        }
      })
      .then((response) => {
        return response.data.deleteRegion;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/region.ts failed on an unknown error while calling deleteRegion.',
      {
        master_list: master_list,
        group_id: group_id,
        company_id: company_id,
        region_id: region_id,
        region_name: region_name
      },
      tracking_id
    );
  }
};

/**
 * Function to archive company Region
 * @param {string} group_id ID of Group to archive region for
 * @param {string} company_id ID of Company to archive region for
 * @param {string} region_id ID of region to archive
 * @returns {void}
 */
export const archiveRegion = async (group_id: string, company_id: string, region_id: string) => {
  try {
    if (group_id && company_id && region_id) {
      await apollo_client
        .mutate({
          mutation: ARCHIVE_REGION_MUTATOR,
          variables: {
            group_id: group_id,
            company_id: company_id,
            region_id: region_id,
            user: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.archiveRegion;
        });
    }
    return;
  } catch (err) {
    const error = `Error while archiving Region in Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: archiveRegion: ${JSON.stringify(error)}.`);
  }
};

/**
 * Function to unarchive company Region
 * @param {string} group_id ID of Group to unarchive region for
 * @param {string} company_id ID of Company to unarchive region for
 * @param {string} region_id ID of region to unarchive
 * @returns {void}
 */
export const unarchiveRegion = async (group_id: string, company_id: string, region_id: string) => {
  try {
    if (group_id && company_id && region_id) {
      await apollo_client
        .mutate({
          mutation: UNARCHIVE_REGION_MUTATOR,
          variables: {
            group_id: group_id,
            company_id: company_id,
            region_id: region_id,
            user: auth.currentUser?.email
          }
        })
        .then((response) => {
          return response.data.unarchiveRegion;
        });
    }
    return;
  } catch (err) {
    const error = `Error while unarchiving Region in Firebase: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: unarchiveRegion: ${JSON.stringify(error)}.`);
  }
};

/**
 * Function to check if a Region can be safely deleted
 * @param {string} group_id ID of Group to check Region for
 * @param {string} company_id ID of Company to check Region for
 * @param {string} region_id ID of region to check
 * @returns {boolean}
 */
export const allowDeleteRegion = async (
  group_id: string,
  company_id: string,
  region_id: string
) => {
  try {
    return await apollo_client
      .query({
        variables: { group_id: group_id, company_id: company_id, region_id: region_id },
        query: ALLOW_DELETE_REGIONS_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.allowDeleteRegion;
      });
  } catch (err) {
    const error = `Error while checking Region for deletion: ${JSON.stringify({
      message: err instanceof Error ? err.message : '',
      stacktrace: err instanceof Error ? err.stack : ''
    })}`;
    throw new Error(`Error: allowDeleteRegion: ${JSON.stringify(error)}.`);
  }
};
